import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API, BASE_API } from 'src/environments/environment';

interface DocumentoConsulta {
  tipo: string;
  path: string;
  nome?: string;
  descricao?: string;
}

interface TipoArquivoOpcao {
  id: number;
  nome: string;
}

interface LinhaUpload {
  selecionado: boolean;
  descricao: string;
  idTipoArquivo: number | null;
  documento: File | null;
}

@Component({
  selector: 'app-upload-documentos-consulta',
  templateUrl: './upload-documentos-consulta.component.html',
  styleUrls: ['./upload-documentos-consulta.component.scss']
})
export class UploadDocumentosConsultaComponent implements OnInit {
  idConsulta = '';
  loading = false;
  salvando = false;
  documentos: DocumentoConsulta[] = [];
  tiposArquivo: TipoArquivoOpcao[] = [];
  linhasUpload: LinhaUpload[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.idConsulta = params.get('idConsulta') || '';
      this.inicializarGridUpload();
      this.carregarTiposArquivo();
      this.carregarDados();
    });
  }

  voltar(): void {
    this.router.navigate(['/consultar-atendimento']);
  }

  adicionarLinha(): void {
    this.linhasUpload.push({
      selecionado: false,
      descricao: '',
      idTipoArquivo: this.tiposArquivo.length ? this.tiposArquivo[0].id : null,
      documento: null
    });
  }

  removerLinhasSelecionadas(): void {
    this.linhasUpload = this.linhasUpload.filter(x => !x.selecionado);
    if (this.linhasUpload.length === 0) {
      this.adicionarLinha();
    }
  }

  onSelecionarArquivo(event: Event, linha: LinhaUpload): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length ? input.files[0] : null;
    if (file && file.type !== 'application/pdf') {
      this.util.toasterShowError('Atenção', 'Por favor, selecione apenas arquivos PDF.');
      linha.documento = null;
      if (input) {
        input.value = '';
      }
      return;
    }
    linha.documento = file;

    if (input) {
      input.value = '';
    }
  }

  enviarDocumentos(): void {
    if (!this.idConsulta) {
      this.util.toasterShowError('Atenção', 'Consulta inválida para upload.');
      return;
    }

    const linhasComArquivo = this.linhasUpload.filter(x => !!x.documento);
    if (!linhasComArquivo.length) {
      this.util.toasterShowError('Atenção', 'Adicione ao menos um arquivo para envio.');
      return;
    }

    const linhaSemTipo = linhasComArquivo.find(x => !x.idTipoArquivo || x.idTipoArquivo <= 0);
    if (linhaSemTipo) {
      this.util.toasterShowError('Atenção', 'Selecione o tipo de arquivo em todas as linhas com arquivo.');
      return;
    }

    const formData = new FormData();
    formData.append('idConsulta', this.idConsulta);
    linhasComArquivo.forEach((linha, index) => {
      formData.append(`ARQUIVOS[${index}][DESCRICAO]`, linha.descricao || '');
      formData.append(`ARQUIVOS[${index}][ID_TIPO_ARQUIVO]`, String(linha.idTipoArquivo || 0));
      formData.append(`ARQUIVOS[${index}][ID_ARQUIVO]`, `${linha.documento!.name}-${index}`);
      formData.append('Documento', linha.documento as Blob, linha.documento!.name);
    });

    this.salvando = true;
    this.util.showLoading();
    this.ajaxService.postFile(API + 'empresa/Consulta/UploadDocumentosSalvar', formData).subscribe({
      next: (response) => {
        this.salvando = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível enviar os documentos.');
          return;
        }

        this.inicializarGridUpload();
        this.util.toasterShowSuccess('Sucesso', 'Documentos enviados com sucesso.');
        this.carregarDados();
      },
      error: () => {
        this.salvando = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao enviar documentos.');
      }
    });
  }

  resolverUrlDocumento(path: string): string {
    const valor = String(path || '').trim();
    if (!valor) return '';
    if (valor.startsWith('http://') || valor.startsWith('https://')) return valor;
    return `${BASE_API}${valor.startsWith('/') ? '' : '/'}${valor}`;
  }

  getNomeArquivo(linha: LinhaUpload): string {
    return linha.documento ? linha.documento.name : 'Nenhum arquivo escolhido';
  }

  private inicializarGridUpload(): void {
    this.linhasUpload = [];
    this.adicionarLinha();
  }

  private carregarTiposArquivo(): void {
    this.ajaxService.get(`${API}empresa/Consulta/ConsultarTiposArquivo`).subscribe({
      next: (response) => {
        const lista = this.normalizarLista(response?.data);
        this.tiposArquivo = lista
          .map((item: any) => this.util.converterParaLowercase(item))
          .filter((item: any) => item?.id && item?.descricao)
          .map((item: any) => ({
            id: Number(item.id),
            nome: item.descricao
          }));

        if (this.tiposArquivo.length) {
          this.linhasUpload.forEach(linha => {
            if (!linha.idTipoArquivo) {
              linha.idTipoArquivo = this.tiposArquivo[0].id;
            }
          });
        }
      },
      error: () => {
        this.tiposArquivo = [];
      }
    });
  }

  private carregarDados(): void {
    if (!this.idConsulta) {
      this.documentos = [];
      return;
    }

    this.loading = true;
    this.util.showLoading();
    this.ajaxService
      .get(`${API}empresa/Consulta/UploadDocs?idConsulta=${encodeURIComponent(this.idConsulta)}`)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.util.closeLoading();

          if (response?.hasErrors) {
            this.documentos = [];
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar os documentos.');
            return;
          }

          const lista = this.normalizarLista(response?.data?.documentos);

          this.documentos = lista
            .map((item: any) => this.util.converterParaLowercase(item))
            .filter((item: any) => !!item?.path)
            .map((item: any) => ({
              tipo: item.tipo || 'Documento',
              path: item.path,
              nome: item.nome || '',
              descricao: item.descricao || ''
            }));
        },
        error: () => {
          this.loading = false;
          this.util.closeLoading();
          this.documentos = [];
          this.util.toasterShowError('Erro', 'Erro ao carregar documentos da consulta.');
        }
      });
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') return Object.values(data);
    return [];
  }
}
